// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what a ready card's buttons do (ReadyActionSchema in packages/schema AssistantEngagement),
// wherever the card is: the corner pop-up, the "From Konvo" list or the phone sheet. Send reply
// waits 5 s behind an Undo toast before it posts; the rest open Mail, the quick event card, the
// task form, the brief or the Assistant chat, close a promise, or run the pop-up's own action.
// Afterwards the outcome is reported (corner: 'used' through Corner; engine: the item outcome)
// and the list loads again. ✕ on an entry is a dismissal for both.

import {Routes} from '@app/app/Routes';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import AskKonvo from '@app/features/konvo_ask/state/AskKonvo';
import {openQuickEvent} from '@app/features/konvo_calendar/components/KonvoQuickEventCard';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import Corner from '@app/features/konvo_corner/state/Corner';
import ReadyList from '@app/features/konvo_corner/state/ReadyList';
import {isTimePassed} from '@app/features/konvo_corner/state/ReadyListModel';
import KonvoMail from '@app/features/konvo_mail/state/KonvoMail';
import {mailTaskOrigin} from '@app/features/konvo_tasks/state/TaskDraft';
import HomeProactive from '@app/features/konvo_today/state/HomeProactive';
import WaitingStore from '@app/features/konvo_waiting/state/WaitingStore';
import {KonvoCreateTaskModal} from '@app/features/messaging/components/modals/KonvoCreateTaskModal';
import {openExternalLink} from '@app/features/messaging/utils/ExternalLinkUtils';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ArrowCounterClockwiseIcon, PaperPlaneRightIcon} from '@app/features/ui/icons/Icons';
import type {CornerSuggestion, ReadyAction, ReadyItem} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {when} from 'mobx';
import {createElement} from 'react';

const logger = new Logger('ReadyActions');

/** Send reply waits this long behind its Undo toast. */
export const SEND_UNDO_MS = 5_000;
/** How long Mail gets to open the mail and Konvo's take on it before the card is left closed. */
const MAIL_CARD_WAIT_MS = 10_000;

const T = {
	sendingTo: msg({
		message: 'Sending your reply to {name}…',
		comment:
			'Toast while a reply Konvo wrote waits a few seconds before it is sent (Undo is offered). name is a person.',
	}),
	sending: msg({message: 'Sending your reply…', comment: 'Toast while a reply Konvo wrote waits before it is sent.'}),
	undo: msg({message: 'Undo', comment: 'Toast button: do not send the reply after all.'}),
	dismiss: msg({message: 'Dismiss', comment: 'Accessible name of the close button on a toast.'}),
	sentTo: msg({
		message: 'Reply sent to {name}',
		comment: 'Toast after a reply Konvo wrote was sent. name is a person.',
	}),
	sent: msg({message: 'Reply sent', comment: 'Toast after a reply Konvo wrote was sent.'}),
	sendFailed: msg({
		message: 'Could not send the reply. It is back in your list.',
		comment: 'Toast when sending a reply from the From Konvo list failed.',
	}),
	timePassed: msg({
		message: 'That time has passed. Open the mail to pick another.',
		comment:
			'Toast when a reply Konvo wrote was not sent because the meeting time it offers has already started. The mail opens so the person can choose a new time.',
	}),
	promiseFailed: msg({
		message: 'Could not update the promise. Try again.',
		comment: 'Toast when marking a promise done or not mine from the From Konvo list failed.',
	}),
	sentNote: msg({message: 'Sent', comment: 'Done today entry note in the From Konvo list: the reply was sent.'}),
	doneNote: msg({message: 'Done', comment: 'Done today entry note in the From Konvo list: the promise was kept.'}),
	droppedNote: msg({
		message: 'Not mine',
		comment: 'Done today entry note in the From Konvo list: the promise was set aside as not yours.',
	}),
};

/** Where the button was pressed: the pop-up closes itself first (Corner.closeForAction). */
export type ReadyFrom = 'popup' | 'list';

/** Report 'used' for whatever the entry stands for, and make sure Home forgets an engine item. */
function reportUsed(item: ReadyItem, from: ReadyFrom): void {
	if (item.suggestion) Corner.reportUsed(item.suggestion, from === 'list' ? 'ready:list' : undefined);
	if (item.engine_item_id) {
		KonvoConnections.reportProactiveOutcome(item.engine_item_id, 'used');
		HomeProactive.finish(item.engine_item_id);
	}
}

/** Leave the list for what the button opened (a page, a form): the list closes. */
function leave(): void {
	ReadyList.close();
}

function done(item: ReadyItem, from: ReadyFrom, note?: string): void {
	reportUsed(item, from);
	if (note) ReadyList.complete(item, note);
	else ReadyList.remove(item);
	ReadyList.refreshSoon(1500);
}

/** Opens the mail on the Mail page and, once Mail has Konvo's take on it, Konvo's card. */
export function openMailWithCard(mailId: string): void {
	KonvoMail.selectInbox(mailId);
	RouterUtils.transitionTo(`${Routes.EMAILS}?open=${encodeURIComponent(mailId)}`);
	void when(() => Corner.pagePeek != null && KonvoMail.selectedId === mailId, {timeout: MAIL_CARD_WAIT_MS})
		.then(() => Corner.pagePeek?.open())
		.catch(() => undefined);
}

function openAssistantChat(): void {
	void AskKonvo.ensureChannel()
		.then((channelId) => {
			if (channelId) RouterUtils.transitionTo(Routes.dmChannel(channelId));
		})
		.catch((error) => logger.warn('No Assistant DM to open', error));
}

function sendReply(i18n: I18n, item: ReadyItem, from: ReadyFrom): void {
	const reply = item.card.reply;
	const suggestionId = item.suggestion?.id;
	if (!reply || !suggestionId) {
		// Nothing the server can send for this entry: open it in Mail instead.
		const mailId = reply?.mail_id;
		if (mailId) {
			leave();
			openMailWithCard(mailId);
			done(item, from);
		}
		return;
	}
	const name = reply.to_name?.trim() || null;
	ReadyList.remove(item);
	let cancelled = false;
	let toastId: string | null = null;
	const timer = setTimeout(() => {
		if (toastId) ToastCommands.destroyToast(toastId);
		if (cancelled) return;
		void http
			.post(Endpoints.KONVO_CORNER_SEND_REPLY(suggestionId))
			.then(() => {
				done(item, from, i18n._(T.sentNote));
				// The reply answered a waiting item: the Waiting chip, bar and Home card catch up.
				if (item.kind === 'waiting_on_you') void WaitingStore.refresh({force: true});
				ToastCommands.success(name ? i18n._(T.sentTo, {name}) : i18n._(T.sent));
			})
			.catch((error) => {
				if (isTimePassed(error)) {
					// The meeting time in the reply has started: the mail opens to pick another one.
					ToastCommands.error(i18n._(T.timePassed));
					leave();
					openMailWithCard(reply.mail_id);
					done(item, from);
					return;
				}
				logger.warn('Could not send the ready reply', error);
				ReadyList.restore(item);
				ToastCommands.error(i18n._(T.sendFailed));
			});
	}, SEND_UNDO_MS);
	toastId = ToastCommands.createToast({
		type: 'info',
		// Closed by the timer above; the toast's own clock pauses under the pointer.
		timeout: SEND_UNDO_MS + 2_000,
		leading: createElement(PaperPlaneRightIcon, {size: 18, weight: 'bold'}),
		children: name ? i18n._(T.sendingTo, {name}) : i18n._(T.sending),
		action: {
			label: i18n._(T.undo),
			icon: createElement(ArrowCounterClockwiseIcon, {size: 15}),
			onClick: () => {
				cancelled = true;
				clearTimeout(timer);
				ReadyList.restore(item);
			},
			'data-flx': 'konvo-corner.ready.undo',
		},
		dismissLabel: i18n._(T.dismiss),
	});
}

function closePromise(i18n: I18n, item: ReadyItem, from: ReadyFrom, action: ReadyAction): void {
	const id = action.commitment_id;
	if (!id) return;
	const status = action.kind === 'promise_done' ? 'completed' : 'dropped';
	const note = i18n._(status === 'completed' ? T.doneNote : T.droppedNote);
	ReadyList.complete(item, note);
	void http
		.post(Endpoints.KONVO_SCHEDULED_PROMISE(id), {body: {status}})
		.then(() => done(item, from, note))
		.catch((error) => {
			logger.warn('Could not close the promise', error);
			ReadyList.restore(item);
			ToastCommands.error(i18n._(T.promiseFailed));
		});
}

function openTaskForm(item: ReadyItem, from: ReadyFrom, action: ReadyAction, anchorName: string): void {
	const mailId = action.mail_id ?? item.card.reply?.mail_id ?? null;
	const name = action.task_name ?? anchorName;
	const open = (origin: ReturnType<typeof mailTaskOrigin> | null) =>
		ModalCommands.push(
			ModalCommands.modal(() =>
				createElement(KonvoCreateTaskModal, {
					initialName: name,
					draft: {
						name,
						description: item.card.lines.map((line) => line.title).join('\n'),
						list_id: null,
						assignee_id: null,
						due_date_ms: null,
						priority: null,
						reason: item.card.note ?? null,
					},
					origin,
					source: 'mail',
					onClose: () => ModalCommands.pop(),
				}),
			),
		);
	leave();
	done(item, from);
	if (!mailId) {
		open(null);
		return;
	}
	// The form names the mail it is made from when the mail can be read.
	void KonvoMail.loadDetail(mailId, false, true)
		.then((detail) => open(detail ? mailTaskOrigin(detail) : null))
		.catch(() => open(null));
}

/** Do what the button says for this entry. */
export function runReadyAction(i18n: I18n, item: ReadyItem, action: ReadyAction, from: ReadyFrom): void {
	switch (action.kind) {
		case 'send_reply':
			sendReply(i18n, item, from);
			return;
		case 'open_mail': {
			const mailId = action.mail_id ?? item.card.reply?.mail_id;
			if (!mailId) return;
			leave();
			openMailWithCard(mailId);
			done(item, from);
			return;
		}
		case 'add_event': {
			const event = action.event;
			if (!event) return;
			leave();
			// The card or list the button was on closes: the quick event card points at the icon instead.
			const icon = document.querySelector<HTMLElement>('[data-flx="konvo-corner.icon"]');
			openQuickEvent({
				prefill: {subject: event.title, start: event.start, end: event.end, attendees: [], people: [], hint: null},
				channel: null,
				target: icon,
			});
			done(item, from);
			return;
		}
		case 'mail_task':
			openTaskForm(item, from, action, item.card.headline);
			return;
		case 'promise_done':
		case 'promise_drop':
			closePromise(i18n, item, from, action);
			return;
		case 'open_brief': {
			const channelId = action.channel_id ?? item.suggestion?.action.channel_id;
			const messageId = action.message_id ?? item.suggestion?.action.message_id;
			leave();
			if (channelId)
				RouterUtils.transitionTo(
					messageId ? Routes.dmChannelMessage(channelId, messageId) : Routes.dmChannel(channelId),
				);
			else openAssistantChat();
			done(item, from);
			return;
		}
		case 'join':
			if (action.url) openExternalLink(action.url);
			done(item, from);
			return;
		case 'open_dm':
			leave();
			openAssistantChat();
			done(item, from);
			return;
		case 'run': {
			leave();
			const suggestion = item.suggestion;
			if (suggestion) {
				// Corner reports 'used' itself when it runs the pop-up's action.
				Corner.runFromList(suggestion, from === 'list' ? 'ready:list' : null);
				if (item.engine_item_id) KonvoConnections.reportProactiveOutcome(item.engine_item_id, 'used');
				ReadyList.remove(item);
				ReadyList.refreshSoon(1500);
				return;
			}
			openAssistantChat();
			done(item, from);
			return;
		}
	}
}

/** The pop-up's button: close the pop-up first, then act on it as a list entry. */
export function runFromPopup(i18n: I18n, suggestion: CornerSuggestion, action: ReadyAction): void {
	const closed = Corner.closeForAction(`ready:${action.kind}`) ?? suggestion;
	const card = closed.ready ?? suggestion.ready;
	if (!card) return;
	runReadyAction(
		i18n,
		{
			id: `c:${closed.id}`,
			source: 'corner',
			kind: closed.kind,
			at: new Date().toISOString(),
			state: 'ready',
			card,
			suggestion: closed,
		},
		action,
		'popup',
	);
}

/** ✕ on a list entry: gone from the list, reported as dismissed. */
export function dismissReadyItem(item: ReadyItem): void {
	if (item.suggestion) Corner.reportDismissed(item.suggestion);
	if (item.engine_item_id) HomeProactive.dismiss(item.engine_item_id);
	ReadyList.remove(item);
	ReadyList.refreshSoon(1500);
}
